// src/features/learner/components/ContentRenderers/ImageMCQRenderer.js
import React, { useMemo } from 'react';
import { AudioPlayer } from '../../../../features/MediaLibrary';
import './../../../../features/units/ImageMCQ/ImageMCQ.css';

export const ImageMCQRenderer = ({
    unit,
    userAnswers,
    showFeedback,
    onAnswerSelect,
    onCheckAnswer,
    onMarkCompleted,
    onResetUnit,
    getRandomizedOptions
}) => {
    const content = useMemo(() => {
        if (!unit?.content) return {};
        try {
            return typeof unit.content === 'string'
                ? JSON.parse(unit.content)
                : unit.content;
        } catch {
            return {};
        }
    }, [unit?.content]);

    const { stemText = '', stemAudio = '', options = [], generalFeedback = '' } = content;

    const showFeedbackForUnit = showFeedback?.[unit.id];
    const userAnswer = userAnswers?.[unit.id];

    // Randomise option order once per session (same hook as MCQRenderer)
    const displayOptions = useMemo(() => {
        if (getRandomizedOptions) return getRandomizedOptions(unit.id, options);
        return options;
    }, [getRandomizedOptions, unit.id, options]);

    const handleOptionClick = (optionId) => {
        if (showFeedbackForUnit) return; // locked after checking
        if (onAnswerSelect) onAnswerSelect(unit.id, optionId);
    };

    const handleCheckAnswer = () => {
        const isCorrect = options.find(o => o.id === userAnswer)?.isCorrect;
        if (onMarkCompleted) onMarkCompleted(unit.id, null, isCorrect ? 'correct' : 'wrong');
        if (onCheckAnswer) onCheckAnswer(unit.id);
    };

    // Derive per-option CSS class after feedback is shown
    const optionClass = (opt) => {
        const classes = ['option-card'];
        if (showFeedbackForUnit) {
            classes.push('answered');
            if (opt.isCorrect) classes.push('reveal-correct');
            else if (userAnswer === opt.id) classes.push('reveal-incorrect');
        } else if (userAnswer === opt.id) {
            classes.push('selected');
        }
        return classes.join(' ');
    };

    const selectedOption = options.find(o => o.id === userAnswer);
    const isCorrect = selectedOption?.isCorrect;

    return (
        <div className="image-mcq-content">
                        {/* Stem */}
            {(stemText || stemAudio) && (
                <div className="stem-area">
                    {stemText && <p className="stem-text">{stemText}</p>}
                    {stemAudio && <AudioPlayer audioUrl={stemAudio} />}
                </div>
            )}

            {/* 2×2 image grid */}
            <div className="options-grid">
                {displayOptions.map((opt, i) => (
                    <div
                        key={opt.id}
                        className={optionClass(opt)}
                        onClick={() => handleOptionClick(opt.id)}
                        role="radio"
                        aria-checked={userAnswer === opt.id}
                        tabIndex={showFeedbackForUnit ? -1 : 0}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') handleOptionClick(opt.id);
                        }}
                    >
                        {opt.imageUrl
                            ? <img
                                className="option-image"
                                src={opt.imageUrl}
                                alt={`Option ${i + 1}`}
                                draggable={false}
                              />
                            : <div className="option-image-missing">🖼️</div>
                        }
                        {/* Per-option feedback — shown after checking */}
                        {showFeedbackForUnit && opt.feedback && userAnswer === opt.id && (
                            <div className="option-feedback">{opt.feedback}</div>
                        )}
                    </div>
                ))}
            </div>

            {/* Check button / feedback block */}
            {!showFeedbackForUnit ? (
                <button
                    className="check-button"
                    onClick={handleCheckAnswer}
                    disabled={!userAnswer}
                >
                    Check Answer
                </button>
            ) : (
                <div className={`feedback-container ${isCorrect ? 'correct' : 'incorrect'}`}>
                    <div className="feedback-header">
                        {isCorrect ? '✅ Correct!' : '❌ Incorrect'}
                    </div>
                    {generalFeedback && (
                        <div className="feedback-text general-feedback">{generalFeedback}</div>
                    )}
                    {!isCorrect && onResetUnit && (
                        <div style={{ textAlign: 'center', marginTop: '12px' }}>
                            <button
                                className="reset-button"
                                onClick={() => onResetUnit(unit.id)}
                                title="Try again"
                                style={{
                                    padding: '8px 12px', fontSize: '18px',
                                    backgroundColor: '#6c757d', color: 'white',
                                    border: 'none', borderRadius: '50%', cursor: 'pointer',
                                    width: '40px', height: '40px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    margin: '0 auto', transition: 'all 0.2s ease',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                            >
                                🔄
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ImageMCQRenderer;